import React, { useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Typography,
  Popconfirm,
  message,
  Drawer,
  Divider,
  Switch,
  List,
  Avatar,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  CommentOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../utils/apiClient';
import { AppSelector } from '../components/AppSelector';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const FeatureRequests = () => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [commentModalVisible, setCommentModalVisible] = useState(false);
  const [editCommentModalVisible, setEditCommentModalVisible] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();
  const [editCommentForm] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch single feature request with comments
  const { data: featureDetails, isLoading: featureLoading } = useQuery(
    ['feature-details', selectedRequest?._id],
    async () => {
      if (!selectedRequest?._id) return null;
      const response = await apiClient.get(`/feature-requests/${selectedRequest._id}`);
      return response.data;
    },
    {
      enabled: !!selectedRequest?._id && viewDrawerVisible,
    }
  );

  // Fetch feature requests
  const { data, isLoading, refetch } = useQuery(
    [
      'feature-requests',
      currentPage,
      searchText,
      statusFilter,
      categoryFilter,
      priorityFilter,
      typeFilter,
      sortBy,
      sortOrder,
    ],
    async () => {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        search: searchText,
        status: statusFilter,
        category: categoryFilter,
        priority: priorityFilter,
        type: typeFilter,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      const response = await apiClient.get(`/feature-requests?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await apiClient.put(`/feature-requests/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Feature request updated successfully');
        queryClient.invalidateQueries('feature-requests');
        queryClient.invalidateQueries('dashboard-stats');
        setEditModalVisible(false);
        form.resetFields();
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/feature-requests/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Feature request deleted successfully');
        queryClient.invalidateQueries('feature-requests');
        queryClient.invalidateQueries('dashboard-stats');
      },
    }
  );

  // Add comment mutation
  const addCommentMutation = useMutation(
    async (commentData) => {
      const response = await apiClient.post('/comments', commentData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Comment added successfully');
        queryClient.invalidateQueries('feature-requests');
        queryClient.invalidateQueries('feature-details');
        setCommentModalVisible(false);
        commentForm.resetFields();
      },
    }
  );

  // Update comment mutation
  const updateCommentMutation = useMutation(
    async ({ id, data }) => {
      const response = await apiClient.put(`/comments/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Comment updated successfully');
        queryClient.invalidateQueries('feature-requests');
        queryClient.invalidateQueries('feature-details');
        setEditCommentModalVisible(false);
        editCommentForm.resetFields();
      },
    }
  );

  // Delete comment mutation
  const deleteCommentMutation = useMutation(
    async (id) => {
      const response = await apiClient.delete(`/comments/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Comment deleted successfully');
        queryClient.invalidateQueries('feature-requests');
        queryClient.invalidateQueries('feature-details');
      },
    }
  );

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      approved: 'green',
      rejected: 'red',
      'in-progress': 'blue',
      completed: 'blue',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'green',
      medium: 'orange',
      high: 'red',
      critical: 'magenta',
    };
    return colors[priority] || 'default';
  };

  const getTypeColor = (type) => {
    const colors = {
      'New feature': 'blue',
      Trending: 'orange',
      'Bug report': 'red',
    };
    return colors[type] || 'default';
  };

  const handleEdit = (record) => {
    setSelectedRequest(record);
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      submitterName: record.submitterName,
      submitterEmail: record.submitterEmail,
      myshopifyDomain: record.myshopifyDomain || '',
      priority: record.priority,
      status: record.status,
      category: record.category,
      type: record.type,
      isPublic: record.isPublic,
      adminNotes: record.adminNotes || '',
    });
    setEditModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedRequest(record);
    setViewDrawerVisible(true);
  };

  const handleComment = (record) => {
    setSelectedRequest(record);
    setCommentModalVisible(true);
  };

  const handleAddComment = (values) => {
    addCommentMutation.mutate({
      featureRequestId: selectedRequest._id,
      content: values.content,
      isPublic: values.isPublic || false,
    });
  };

  const handleEditComment = (comment) => {
    setSelectedComment(comment);
    editCommentForm.setFieldsValue({
      content: comment.content,
      isPublic: comment.isPublic,
      adminNotes: comment.adminNotes || '',
    });
    setEditCommentModalVisible(true);
  };

  const handleUpdateComment = (values) => {
    updateCommentMutation.mutate({
      id: selectedComment._id,
      data: values,
    });
  };

  const handleDeleteComment = (commentId) => {
    deleteCommentMutation.mutate(commentId);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleUpdate = (values) => {
    updateMutation.mutate({
      id: selectedRequest._id,
      data: values,
    });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    refetch();
  };

  const handleReset = () => {
    setSearchText('');
    setStatusFilter('');
    setCategoryFilter('');
    setPriorityFilter('');
    setTypeFilter('');
    setSortBy('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Button type="link" onClick={() => handleView(record)} style={{ padding: 0, height: 'auto', fontWeight: 500 }}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Submitter',
      dataIndex: 'submitterName',
      key: 'submitterName',
    },
    {
      title: 'Email',
      dataIndex: 'submitterEmail',
      key: 'submitterEmail',
      responsive: ['md'],
    },
    {
      title: 'Shopify Domain',
      dataIndex: 'myshopifyDomain',
      key: 'myshopifyDomain',
      responsive: ['lg'],
      render: (domain) =>
        domain ? (
          <span style={{ color: '#52c41a', fontFamily: 'monospace' }}>
            {domain.includes('.myshopify.com') ? domain : `${domain}.myshopify.com`}
          </span>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      responsive: ['lg'],
      render: (cate) => <span>{cate}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={getTypeColor(type)} className="priority-tag">
          {type}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => (
        <Tag color={getPriorityColor(priority)} className="priority-tag">
          {priority.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} className="status-tag">
          {status.replace('-', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Upvotes',
      dataIndex: 'upvoteCount',
      key: 'upvoteCount',
      render: (count) => <span style={{ fontWeight: 'bold', color: '#1890ff' }}>👍 {count || 0}</span>,
      sorter: true,
    },
    {
      title: 'Public',
      dataIndex: 'isPublic',
      key: 'isPublic',
      render: (isPublic) => <Tag color={isPublic ? 'green' : 'default'}>{isPublic ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      responsive: ['lg'],
      sorter: true,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => new Date(date).toLocaleDateString(),
      responsive: ['xl'],
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} title="View" />
          <Button type="text" icon={<CommentOutlined />} onClick={() => handleComment(record)} title="Add Comment" />
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
          <Popconfirm
            title="Are you sure you want to delete this feature request?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} className="page-title">
        Feature Requests
      </Title>

      <Card>
        <div className="table-actions">
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Search requests..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Select
                placeholder="Status"
                value={statusFilter || undefined}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="approved">Approved</Select.Option>
                <Select.Option value="rejected">Rejected</Select.Option>
                <Select.Option value="in-progress">In Progress</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <AppSelector value={categoryFilter || undefined} onChange={setCategoryFilter} />
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Select
                placeholder="Type"
                value={typeFilter || undefined}
                onChange={setTypeFilter}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="New feature">New Feature</Select.Option>
                <Select.Option value="Trending">Trending</Select.Option>
                <Select.Option value="Bug report">Bug Report</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={8} md={4}>
              <Select
                placeholder="Priority"
                value={priorityFilter || undefined}
                onChange={setPriorityFilter}
                style={{ width: '100%' }}
                allowClear
              >
                <Select.Option value="low">Low</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="high">High</Select.Option>
                <Select.Option value="critical">Critical</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space>
                <Button type="primary" onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleReset}>Reset</Button>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()} title="Refresh" />
              </Space>
            </Col>
          </Row>

          {/* Sorting Controls - New Line for Better Layout */}
          <Divider style={{ margin: '16px 0' }} />
          <Row gutter={[16, 16]} style={{ width: '100%' }}>
            <Col xs={24} sm={6} md={4}>
              <div
                style={{
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#666',
                }}
              >
                Sort by:
              </div>
              <Select value={sortBy} onChange={setSortBy} style={{ width: '100%' }}>
                <Select.Option value="createdAt">Created Time</Select.Option>
                <Select.Option value="updatedAt">Updated Time</Select.Option>
                <Select.Option value="title">Title</Select.Option>
                <Select.Option value="priority">Priority</Select.Option>
                <Select.Option value="upvoteCount">Upvotes</Select.Option>
                <Select.Option value="submitterName">Submitter</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <div
                style={{
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#666',
                }}
              >
                Order:
              </div>
              <Select value={sortOrder} onChange={setSortOrder} style={{ width: '100%' }}>
                <Select.Option value="desc">Newest First</Select.Option>
                <Select.Option value="asc">Oldest First</Select.Option>
              </Select>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={data?.featureRequests}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize,
            total: data?.total,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: 800 }}
          onChange={(pagination, filters, sorter) => {
            // Handle table sorting
            if (sorter && sorter.field) {
              setSortBy(sorter.field);
              setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
            }
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Feature Request"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={updateMutation.isLoading}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
                rules={[
                  {
                    required: true,
                    message: 'Please input title!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              {
                required: true,
                message: 'Please input description!',
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="submitterName"
                label="Submitter Name"
                rules={[
                  {
                    required: true,
                    message: 'Please input submitter name!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="submitterEmail"
                label="Submitter Email"
                rules={[
                  {
                    required: true,
                    message: 'Please input submitter email!',
                  },
                  {
                    type: 'email',
                    message: 'Please enter valid email!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[
                  {
                    required: true,
                    message: 'Please select priority!',
                  },
                ]}
              >
                <Select>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="medium">Medium</Select.Option>
                  <Select.Option value="high">High</Select.Option>
                  <Select.Option value="critical">Critical</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: 'Please select status!',
                  },
                ]}
              >
                <Select>
                  <Select.Option value="pending">Pending</Select.Option>
                  <Select.Option value="approved">Approved</Select.Option>
                  <Select.Option value="rejected">Rejected</Select.Option>
                  <Select.Option value="in-progress">In Progress</Select.Option>
                  <Select.Option value="completed">Completed</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isPublic" label="Public Visibility" valuePropName="checked">
                <Select>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="adminNotes" label="Admin Notes">
            <TextArea rows={3} placeholder="Internal notes..." />
          </Form.Item>
        </Form>
        {selectedRequest && selectedRequest.images ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selectedRequest.images.map((img) => {
              return (
                <a href={img} target="_blank" key={img} rel="noreferrer">
                  <img
                    src={img}
                    style={{
                      width: 200,
                      height: 120,
                      objectFit: 'contain',
                    }}
                  />
                </a>
              );
            })}
          </div>
        ) : (
          ''
        )}
      </Modal>

      {/* Add Comment Modal */}
      <Modal
        title={`Add Comment - ${selectedRequest?.title}`}
        open={commentModalVisible}
        onCancel={() => {
          setCommentModalVisible(false);
          commentForm.resetFields();
        }}
        onOk={() => commentForm.submit()}
        confirmLoading={addCommentMutation.isLoading}
      >
        <Form form={commentForm} layout="vertical" onFinish={handleAddComment}>
          <Form.Item
            name="content"
            label="Comment"
            rules={[
              {
                required: true,
                message: 'Please enter a comment!',
              },
              {
                min: 5,
                message: 'Comment must be at least 5 characters!',
              },
            ]}
          >
            <TextArea rows={4} placeholder="Add your comment here..." maxLength={1000} showCount />
          </Form.Item>

          <Form.Item name="isPublic" valuePropName="checked" label="Make this comment public">
            <Switch checkedChildren="Public" unCheckedChildren="Private" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Comment Modal */}
      <Modal
        title="Edit Comment"
        open={editCommentModalVisible}
        onCancel={() => {
          setEditCommentModalVisible(false);
          editCommentForm.resetFields();
        }}
        onOk={() => editCommentForm.submit()}
        confirmLoading={updateCommentMutation.isLoading}
        width={600}
      >
        <Form form={editCommentForm} layout="vertical" onFinish={handleUpdateComment}>
          <Form.Item
            name="content"
            label="Comment"
            rules={[
              {
                required: true,
                message: 'Please enter a comment!',
              },
              {
                min: 5,
                message: 'Comment must be at least 5 characters!',
              },
            ]}
          >
            <TextArea rows={4} placeholder="Edit your comment here..." maxLength={1000} showCount />
          </Form.Item>

          <Form.Item name="isPublic" valuePropName="checked" label="Public visibility">
            <Switch checkedChildren="Public" unCheckedChildren="Private" />
          </Form.Item>

          <Form.Item name="adminNotes" label="Admin Notes (Internal)">
            <TextArea rows={2} placeholder="Internal notes about this comment..." maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* View Drawer */}
      <Drawer
        title="Feature Request Details"
        placement="right"
        onClose={() => setViewDrawerVisible(false)}
        open={viewDrawerVisible}
        width={700}
      >
        {selectedRequest && (
          <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4}>{selectedRequest.title}</Title>
                <Space>
                  <Tag color={getPriorityColor(selectedRequest.priority)}>{selectedRequest.priority.toUpperCase()}</Tag>
                  <Tag color={getStatusColor(selectedRequest.status)}>
                    {selectedRequest.status.replace('-', ' ').toUpperCase()}
                  </Tag>
                  <Tag color={selectedRequest.isPublic ? 'green' : 'default'}>
                    {selectedRequest.isPublic ? 'Public' : 'Private'}
                  </Tag>
                </Space>
              </div>

              <div>
                <Text strong>Description:</Text>
                <Paragraph style={{ marginTop: 8 }}>{selectedRequest.description}</Paragraph>
              </div>

              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Submitter:</Text>
                    <div>{selectedRequest.submitterName}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Email:</Text>
                    <div>{selectedRequest.submitterEmail}</div>
                  </Col>
                </Row>
              </div>

              {selectedRequest.myshopifyDomain && (
                <div>
                  <Text strong>Shopify Domain:</Text>
                  <div
                    style={{
                      color: '#52c41a',
                      fontFamily: 'monospace',
                    }}
                  >
                    {selectedRequest.myshopifyDomain.includes('.myshopify.com')
                      ? selectedRequest.myshopifyDomain
                      : `${selectedRequest.myshopifyDomain}.myshopify.com`}
                  </div>
                </div>
              )}

              {selectedRequest && selectedRequest.images ? (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  {selectedRequest.images.map((img) => {
                    return (
                      <a href={img} target="_blank" key={img} rel="noreferrer">
                        <img
                          src={img}
                          style={{
                            width: 200,
                            height: 120,
                            objectFit: 'contain',
                          }}
                        />
                      </a>
                    );
                  })}
                </div>
              ) : (
                ''
              )}
              <div>
                <Text strong>Type:</Text>
                <div>
                  <Tag color={getTypeColor(selectedRequest.type)}>{selectedRequest.type}</Tag>
                </div>
              </div>

              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Category:</Text>
                    <div>{selectedRequest.category || 'General'}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Upvotes:</Text>
                    <div
                      style={{
                        color: '#1890ff',
                        fontWeight: 'bold',
                      }}
                    >
                      👍 {selectedRequest.upvoteCount || 0}
                    </div>
                  </Col>
                </Row>
              </div>

              <div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Created:</Text>
                    <div>{new Date(selectedRequest.createdAt).toLocaleString()}</div>
                  </Col>
                  <Col span={12}>
                    <Text strong>Priority:</Text>
                    <div>
                      <Tag color={getPriorityColor(selectedRequest.priority)}>
                        {selectedRequest.priority.toUpperCase()}
                      </Tag>
                    </div>
                  </Col>
                </Row>
              </div>

              {selectedRequest.adminNotes && (
                <div>
                  <Text strong>Admin Notes:</Text>
                  <Paragraph style={{ marginTop: 8 }}>{selectedRequest.adminNotes}</Paragraph>
                </div>
              )}

              {selectedRequest.lastModifiedBy && (
                <div>
                  <Text type="secondary">Last modified by: {selectedRequest.lastModifiedBy.username}</Text>
                </div>
              )}

              {/* Comments Section */}
              <Divider style={{ margin: '24px 0 16px 0' }} />

              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    Comments ({featureDetails?.comments?.length || 0})
                  </Title>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={() => handleComment(selectedRequest)}
                  >
                    Add Comment
                  </Button>
                </div>

                {featureLoading ? (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                    }}
                  >
                    <Spin size="small" />
                    <div
                      style={{
                        marginTop: '8px',
                        color: '#666',
                      }}
                    >
                      Loading comments...
                    </div>
                  </div>
                ) : featureDetails?.comments && featureDetails.comments.length > 0 ? (
                  <List
                    dataSource={featureDetails.comments}
                    itemLayout="vertical"
                    size="small"
                    renderItem={(comment) => (
                      <List.Item
                        key={comment._id}
                        style={{
                          padding: '12px 16px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '6px',
                          marginBottom: '8px',
                          backgroundColor: comment.isStaffComment ? '#f6ffed' : '#fafafa',
                        }}
                        actions={[
                          <Button
                            key={`edit-button-${comment._id}`}
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEditComment(comment)}
                          >
                            Edit
                          </Button>,
                          <Popconfirm
                            key={`delete-popconfirm-${comment._id}`}
                            title="Are you sure you want to delete this comment?"
                            onConfirm={() => handleDeleteComment(comment._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              key={`delete-popconfirm-button-${comment._id}`}
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                            >
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor: comment.isStaffComment ? '#52c41a' : '#1890ff',
                              }}
                            />
                          }
                          title={
                            <Space>
                              <span
                                style={{
                                  fontWeight: '500',
                                }}
                              >
                                {comment.authorName}
                              </span>
                              {comment.isStaffComment && (
                                <Tag color="green" size="small">
                                  Staff
                                </Tag>
                              )}
                              <Tag color={comment.isPublic ? 'blue' : 'default'} size="small">
                                {comment.isPublic ? 'Public' : 'Private'}
                              </Tag>
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: '#999',
                                }}
                              >
                                {new Date(comment.createdAt).toLocaleString()}
                              </span>
                            </Space>
                          }
                          description={
                            <div
                              style={{
                                marginTop: '8px',
                              }}
                            >
                              <Paragraph
                                style={{
                                  margin: 0,
                                  color: '#262626',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {comment.content}
                              </Paragraph>
                              {comment.adminNotes && (
                                <div
                                  style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#fff7e6',
                                    border: '1px solid #ffd591',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <Text
                                    strong
                                    style={{
                                      fontSize: '12px',
                                      color: '#d46b08',
                                    }}
                                  >
                                    Admin Notes:
                                  </Text>
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#d46b08',
                                      marginTop: '4px',
                                    }}
                                  >
                                    {comment.adminNotes}
                                  </div>
                                </div>
                              )}
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#999',
                      border: '1px dashed #d9d9d9',
                      borderRadius: '6px',
                    }}
                  >
                    <CommentOutlined
                      style={{
                        fontSize: '24px',
                        marginBottom: '8px',
                      }}
                    />
                    <div>No comments yet</div>
                    <div
                      style={{
                        fontSize: '12px',
                        marginTop: '4px',
                      }}
                    >
                      Be the first to add a comment
                    </div>
                  </div>
                )}
              </div>
            </Space>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default FeatureRequests;

